import type { KgInsightThemeType } from '@api/api/kg-insights';
import { Icon } from '@design/components/icon/Icon';
import { highlight } from '@helpers/utils/highlight/highlight';
import { ThemeSearchLogo } from '@terminal/components/theme-search-logo/ThemeSearchLogo';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { ThemeCompanyCount } from '@terminal/views/theme-overview/components/theme-company-count/ThemeCompanyCount';
import { ThemeSubtitle } from '@terminal/views/theme-overview/components/theme-subtitle/ThemeSubtitle';
import clsx from 'clsx';

import styles from './FollowThemeRow.module.scss';

export interface FollowThemeRowProps {
  themeId: string;
  themeName: string;
  isFollowed: boolean;
  highlighted?: string;
  themeType?: KgInsightThemeType;
}

export const FollowThemeRow = ({
  themeId,
  themeName,
  isFollowed,
  highlighted,
  themeType,
}: FollowThemeRowProps) => {
  const { data: theme } = useTheme({ themeId });
  const resolvedThemeType = themeType ?? theme?.type;

  const displayName = highlighted
    ? highlight(themeName, highlighted)
    : themeName;

  return (
    <button
      type="button"
      className={styles.row}
      data-testid={`follow-theme-row-${themeId}`}
      aria-pressed={isFollowed}
    >
      <span
        className={clsx(styles.toggle, {
          [styles.toggleFollowed]: isFollowed,
        })}
        aria-hidden
      >
        <Icon iconName={isFollowed ? 'Checkmark' : 'Add'} size={12} />
      </span>
      <ThemeSearchLogo
        name={themeName}
        logoUrl={theme?.logo_url}
        themeType={themeType}
      />
      <div className={styles.details}>
        <span className={styles.title}>{displayName}</span>
        <span className={styles.subtitle}>
          <ThemeSubtitle type={resolvedThemeType} />
          <ThemeCompanyCount themeId={themeId} themeType={resolvedThemeType} />
        </span>
      </div>
    </button>
  );
};
